import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape PRC-01 publishes, imported rather than restated.
import type { RateCard } from "@tdelivr/api-client/generated/pricing/pricing-configuration.js";

import { boundedNote, PER_FETCH, type BoardRow } from "../board.ts";
import { cardRow, CARD_STATUSES } from "../rate-cards.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * Pricing and rate cards: every version this tenant holds, newest first.
 *
 * Built to `17 WP16 Tenant Admin.dc.html`, page `pricing`, against PRC-01
 * `GET /v1/tenants/{tenantId}/rate-cards`.
 *
 * **This was the sharpest of the eleven absences.** The two reads this service had were one version
 * by `(rateCardId, version)` and the single card *effective* for a triple — and a card awaiting
 * approval is effective at no instant, so no parameter would ever have returned the row the
 * design's own board leads with, `v13 · awaiting approval`.
 *
 * **The `Scope` column carries a product identifier where the design writes a product's name.**
 * Q-102, whose recorded decision is the identifier in Geist Mono with the grid saying it is an
 * identifier rather than a name. `Economy · Nairobi` is two joins rather than one: no service on
 * the platform publishes a name for a `geographyId` at all, which is the same mismatch R-13
 * recorded from the zones page.
 *
 * **Money is written with its currency code and full minor units.** CLAUDE.md section 2, and
 * `money()` places the decimal point by splitting the integer's own decimal spelling rather than by
 * dividing by a hundred. The design's grid draws no money, so the five components are in the detail
 * panel, which is where that file lists a row's published fields.
 *
 * **Two of the design's four tiles belong to services this page does not read, and the other two
 * are counts.** `Surge zones now · 2` is the dynamic-pricing service, whose policy read is by
 * `policyId`; `Promotions · 4` is the promotion service. Neither is an owner of this page.
 * `Live rate card · v12` and `Awaiting approval · v13` are both derivable from the rows held and
 * both would be wrong when the listing is truncated, which is R-04's measurement — so the board
 * says what it fetched instead of summarising it.
 */

const pricingApi = services.pricing__pricing_configuration;

export interface PricingRateCardsProps {
  readonly transport: Transport;
  /** Innermost last: tenant, group, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
  /** The tenant in the path, which this operation takes. */
  readonly tenantId: string;
  /** Injected so the effective-window rule can be exercised without waiting for a date to pass. */
  readonly now?: () => number;
}

interface Loaded {
  readonly cards: readonly RateCard[];
  readonly truncated: boolean;
}

export function PricingRateCards({
  transport,
  crumbs,
  tenantId,
  now,
}: PricingRateCardsProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    const client = pricingApi.createPricingConfigurationClient(transport);
    try {
      /*
       * The status filter goes to the service, and `productId` deliberately does not.
       *
       * This listing publishes both. Choosing a status refetches, as the zones board's lifecycle
       * filter does, because the service can narrow properly. `productId` is a uuid the board has
       * no vocabulary for — it is the very value Q-102 says the page cannot turn into a name — so
       * there is no filter this console could offer over it.
       */
      const answered = await client.listTenantRateCards(
        tenantId,
        status === "" ? undefined : status,
        undefined,
        PER_FETCH,
        undefined,
      );
      setLoaded({
        cards: answered.rateCards,
        truncated: typeof answered.nextAfter === "string" && answered.nextAfter.length > 0,
      });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, ["pricing:configuration:read"]));
      setLoaded(undefined);
    }
  }, [transport, tenantId, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const instant = now === undefined ? Date.now() : now();

  const rows: readonly BoardRow[] | undefined = useMemo(() => {
    if (loaded === undefined) return undefined;
    return loaded.cards.map((card) => cardRow(card, instant));
  }, [loaded, instant]);

  const bounded = boundedNote(
    loaded?.cards.length,
    loaded?.truncated ?? false,
    status === "" ? "rate-card versions" : `${status} rate-card versions`,
    "newest first",
  );

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Pricing and rate cards"
      /*
       * `PRC-01`, where the design's chip reads `PRC-01…04`. The three services that range covers —
       * fare calculation, dynamic pricing and fees — publish no rate card, and two of them are the
       * source of the tiles this page does not draw. The brand board's precedent.
       */
      source="PRC-01"
      // The design's sentence, word for word. Both halves are true of PRC-01: a version is
      // immutable and every revision is a new row, and the transition table admits `scheduled` and
      // `active` only from `approved`. What it does not enforce is *who* approves — Q-106, and the
      // closing note below carries that rather than this sentence.
      description="Rate cards are versioned. A new version needs an approver before it takes effect."
      columns={["Rate card", "Scope", "Effective"]}
      rows={rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="Rate cards appear here as soon as a version is drafted for this tenant."
      primary={{
        label: "New rate card version",
        /*
         * Drawn, and refused. `POST /v1/tenants/{tenantId}/rate-cards` exists and takes a
         * `Configuration` — and three of its required fields are values this form cannot supply.
         * `productId` and `geographyId` are uuids: the design's `Applies to` select offers
         * `All ride services`, `Economy`, `XL`, `Boda` and `All delivery`, and there is no
         * operation that turns any of those words into either identifier (Q-102). Two of the
         * design's own fields have no target either: `Surge cap` belongs to the dynamic-pricing
         * service and `Reason for change` is kept "in the audit log", which this service does not
         * write to.
         *
         * So the command is drawn in the design's geometry with the measured reason on it, rather
         * than sent with identifiers chosen here. An `Idempotency-Key` would have been required —
         * the operation declares the header — and there is no command to put one on.
         */
        refusal:
          "PRC-01's create requires a productId and a geographyId, both uuids, and no operation turns the form's Applies to words into either (Q-102). Its Surge cap belongs to the dynamic-pricing service and its Reason has no field.",
      }}
      filter={{
        unfiltered: "All states",
        options: CARD_STATUSES.map((value) => ({ value, label: value })),
        value: status,
        onChange: setStatus,
      }}
      note={{
        /*
         * The design's `lock` note is replaced and keeps its kind, its glyph and its place.
         *
         * It reads "Uses the approval pattern: the preparer cannot approve their own rate card."
         * The lifecycle is real and the sentence is a safety claim about it that nothing enforces:
         * PRC-01 records no actor on a version — no `createdBy`, no `approvedBy`, no subject — so
         * there is no first pair of eyes to compare a second against. An administrator who believes
         * the platform is preventing self-approval will not check. Q-106.
         */
        kind: "lock",
        glyph: "lock",
        text: "A version is immutable once published and `approved` is the only way into effect. PRC-01 records no actor on a version, so nothing here checks that the approver is a different person.",
      }}
      {...(bounded === undefined ? {} : { bounded })}
      footnote="Every version of every rate card this tenant holds, newest first, whatever its state. The Scope column is the product's identifier: nothing this page reads publishes a product's name."
      exportName="rate-cards"
      detailSubtitle="Pricing and rate cards · PRC-01"
      detailNote="A version is immutable once published and `approved` is the only way into effect. PRC-01 records no actor on a version, so nothing here checks that the approver is a different person."
    />
  );
}
